import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as jobsApi from '../../api/jobs';
import { generateTraceId, logAction } from '../../utils/trace';

// Async thunks
export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async (args = {}, { rejectWithValue, getState }) => {
    const { filters = {}, traceId: inputTraceId } = args;
    const traceId = inputTraceId || generateTraceId();

    // Get active tenant from auth state
    const { auth } = getState();
    const tenantId = auth.activeTenant?.tenant_id;

    try {
      logAction({ type: 'jobs/fetchJobs/pending', meta: { traceId } }, {
        filters,
        tenantId,
      });
      const data = await jobsApi.listJobs(filters, { traceId, tenantId });
      logAction({ type: 'jobs/fetchJobs/fulfilled', meta: { traceId } }, {
        jobCount: data.jobs?.length || 0,
      });
      return data;
    } catch (error) {
      logAction({ type: 'jobs/fetchJobs/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchJobById = createAsyncThunk(
  'jobs/fetchJobById',
  async ({ jobId, traceId: inputTraceId }, { rejectWithValue, getState }) => {
    const traceId = inputTraceId || generateTraceId();

    // Get active tenant from auth state
    const { auth } = getState();
    const tenantId = auth.activeTenant?.tenant_id;

    try {
      logAction({ type: 'jobs/fetchJobById/pending', meta: { traceId } }, {
        jobId,
      });
      const data = await jobsApi.getJob(jobId, { traceId, tenantId });
      logAction({ type: 'jobs/fetchJobById/fulfilled', meta: { traceId } }, {
        jobId: data.job_id,
        status: data.status,
      });
      return data;
    } catch (error) {
      logAction({ type: 'jobs/fetchJobById/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const submitJob = createAsyncThunk(
  'jobs/submitJob',
  async (jobData, { rejectWithValue, getState }) => {
    const traceId = jobData.traceId || generateTraceId();

    // Get active tenant from auth state
    const { auth } = getState();
    const tenantId = auth.activeTenant?.tenant_id;

    try {
      logAction({ type: 'jobs/submitJob/pending', meta: { traceId } }, {
        jobType: jobData.job_type?.name,
      });
      const data = await jobsApi.submitJob(jobData, { traceId, tenantId });
      logAction({ type: 'jobs/submitJob/fulfilled', meta: { traceId } }, {
        jobId: data.job_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'jobs/submitJob/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const cancelJob = createAsyncThunk(
  'jobs/cancelJob',
  async ({ jobId, traceId: inputTraceId }, { rejectWithValue, getState }) => {
    const traceId = inputTraceId || generateTraceId();

    // Get active tenant from auth state
    const { auth } = getState();
    const tenantId = auth.activeTenant?.tenant_id;

    try {
      logAction({ type: 'jobs/cancelJob/pending', meta: { traceId } }, {
        jobId,
      });
      await jobsApi.cancelJob(jobId, { traceId, tenantId });
      logAction({ type: 'jobs/cancelJob/fulfilled', meta: { traceId } }, {
        jobId,
      });
      return jobId;
    } catch (error) {
      logAction({ type: 'jobs/cancelJob/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

const jobsSlice = createSlice({
  name: 'jobs',
  initialState: {
    jobs: [],
    selectedJob: null,
    isLoading: false,
    error: null,
    filters: {
      status: 'all',
    },
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedJob: (state) => {
      state.selectedJob = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchJobs
      .addCase(fetchJobs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobs = action.payload.jobs || [];
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // fetchJobById
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.selectedJob = action.payload;
      })
      // submitJob
      .addCase(submitJob.fulfilled, (state, action) => {
        state.jobs.unshift(action.payload);
      })
      // cancelJob
      .addCase(cancelJob.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.job_id === action.payload);
        if (job) {
          job.status = 'cancel_requested';
        }
      });
  },
});

export const { setFilters, clearSelectedJob } = jobsSlice.actions;
export default jobsSlice.reducer;
